import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { isTauri, invoke } from '@tauri-apps/api/core';
import { Button } from './ui/Button';

/** Mirrors `UpdaterEvent` in desktop/src-tauri/src/updater.rs. `update_available` comes from shells before 0.1.2. */
type UpdateStatus =
  | { kind: 'idle' | 'up_to_date' | 'store_managed'; current_version: string }
  | { kind: 'downloading' | 'update_available'; current: string; latest: string }
  | { kind: 'ready'; current: string; latest: string; needs_authorization: boolean }
  | { kind: 'error'; message: string };

/**
 * Settings card body for the desktop app's updates. Updates download and apply on their own,
 * so this only answers "which version am I on" when someone comes looking; it never pops up.
 */
export function DesktopUpdateStatus() {
  const { t } = useTranslation();
  const [version, setVersion] = useState('');
  const [status, setStatus] = useState<UpdateStatus | null>(null);
  const [checking, setChecking] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!isTauri()) return;
    const handler = (event: Event) => { setStatus((event as CustomEvent<UpdateStatus>).detail); setChecking(false); };
    window.addEventListener('ailechat:updater-status', handler);
    void invoke<{ version: string }>('get_app_info').then(info => setVersion(info.version), () => {});
    void invoke<UpdateStatus>('get_update_status').then(setStatus, () => {});
    return () => window.removeEventListener('ailechat:updater-status', handler);
  }, []);
  if (!isTauri()) return null;

  async function check() {
    setChecking(true); setError('');
    try { await invoke('check_updates'); }
    catch { setError(t('profilePage.desktopUpdate.checkFailed')); }
    finally { setChecking(false); }
  }
  async function restart() {
    setRestarting(true); setError('');
    try { await invoke(status?.kind === 'update_available' ? 'install_update' : 'restart_to_update'); }
    catch { setError(t('profilePage.desktopUpdate.updateFailed')); setRestarting(false); }
  }

  const kind = status?.kind;
  const canCheck = kind !== 'store_managed' && kind !== 'downloading' && kind !== 'ready';
  const message = describe(status, t);

  return <div className="space-y-2">
    <p className="text-sm">{t('profilePage.desktopUpdate.version', {version: version || '—'})}</p>
    <p role="status" className="text-xs text-fg-secondary">{message}</p>
    {error && <p role="alert" className="text-xs text-danger">{error}</p>}
    <div className="flex gap-2">
      {(kind === 'ready' || kind === 'update_available') && <Button size="sm" disabled={restarting} onClick={() => void restart()}>{restarting ? t('profilePage.desktopUpdate.restarting') : t('profilePage.desktopUpdate.restartNow')}</Button>}
      {canCheck && <Button size="sm" variant="secondary" disabled={checking} onClick={() => void check()}>{checking ? t('profilePage.desktopUpdate.checking') : t('profilePage.desktopUpdate.check')}</Button>}
    </div>
  </div>;
}

function describe(status: UpdateStatus | null, t: TFunction): string {
  switch (status?.kind) {
    case 'up_to_date': return t('profilePage.desktopUpdate.upToDate');
    case 'store_managed': return t('profilePage.desktopUpdate.storeManaged');
    case 'downloading': return t('profilePage.desktopUpdate.downloading', {version: status.latest});
    case 'update_available': return t('profilePage.desktopUpdate.available', {version: status.latest});
    case 'ready': return status.needs_authorization
      ? t('profilePage.desktopUpdate.readyAuthorize', {version: status.latest})
      : t('profilePage.desktopUpdate.ready', {version: status.latest});
    case 'error': return t('profilePage.desktopUpdate.retryLater');
    default: return t('profilePage.desktopUpdate.automatic');
  }
}
